import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { useAuthStore } from '@/shared/store/authStore'
import { fetchMe } from '@/features/auth/api'
import { isApiError } from '@/shared/api/http'
import { hasHostShell, usesHostSession } from '@/shared/bridge/env'
import { listenFromHost, postToHost } from '@/shared/bridge/bridge'
import { Skeleton } from '@/shared/ui'

interface Props {
  children: ReactNode
}

// expired: 인증 실패가 확인됨(401). unverified: 통신·서버 오류 등으로 확인하지 못함 → 로그인 정보 유지.
type HostSessionError = { kind: 'expired' | 'unverified'; message: string }

function unverifiedMessage(error: unknown) {
  if (isApiError(error) && error.status === null) return '네트워크 연결을 확인한 후 다시 시도해 주세요.'
  if (isApiError(error) && error.status !== null && error.status >= 500)
    return '서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.'
  return '잠시 후 다시 시도해 주세요.'
}

function HomeContentSkeleton() {
  return (
    <div className="mx-auto grid w-full max-w-5xl gap-4 p-4 lg:gap-5 lg:p-7">
      <section className="overflow-hidden rounded-lg border border-border-subtle bg-surface shadow-card">
        <div className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-28" rounded="full" />
              <Skeleton className="mt-3 h-7 w-48 max-w-full" rounded="md" />
            </div>
            <Skeleton className="size-11 shrink-0" rounded="lg" />
          </div>
          <Skeleton className="mt-5 h-32 w-full" rounded="lg" />
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((key) => (
          <Skeleton key={key} className="h-24 shadow-card" rounded="lg" />
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.35fr_0.9fr]">
        <section className="rounded-lg border border-border-subtle bg-surface p-4 shadow-card">
          <Skeleton className="h-5 w-36" rounded="md" />
          <div className="mt-4 space-y-3">
            {[0, 1, 2, 3].map((key) => (
              <div key={key} className="flex items-center gap-3">
                <Skeleton className="size-10 shrink-0" rounded="md" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-4 w-3/5" rounded="md" />
                  <Skeleton className="mt-2 h-3 w-2/5" rounded="full" />
                </div>
                <Skeleton className="h-6 w-14 shrink-0" rounded="full" />
              </div>
            ))}
          </div>
        </section>

        <section className="hidden rounded-lg border border-border-subtle bg-surface p-4 shadow-card lg:block">
          <Skeleton className="h-5 w-28" rounded="md" />
          <Skeleton className="mt-4 h-36 w-full" rounded="lg" />
          <div className="mt-4 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} className="h-16" rounded="md" />
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

function AppSessionSkeleton({ contentOnly = false }: { contentOnly?: boolean }) {
  if (contentOnly) {
    return (
      <div className="app-viewport overflow-hidden bg-bg" aria-busy="true" role="status">
        <span className="sr-only">화면을 불러오는 중입니다.</span>
        <main className="h-full overflow-hidden">
          <HomeContentSkeleton />
        </main>
      </div>
    )
  }

  return (
    <div className="app-viewport flex overflow-hidden bg-bg" aria-busy="true" role="status">
      <span className="sr-only">화면을 불러오는 중입니다.</span>
      <aside className="hidden w-[248px] shrink-0 flex-col border-r border-border-subtle bg-surface lg:flex">
        <div className="flex h-18 items-center gap-3 border-b border-border-subtle px-4">
          <Skeleton className="size-9 shrink-0" rounded="lg" />
          <Skeleton className="h-7 w-28" rounded="md" />
        </div>
        <nav className="flex-1 space-y-2 px-3 py-4">
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <div key={key} className="flex h-11 items-center gap-3 rounded-md px-3">
              <Skeleton className="size-5 shrink-0" rounded="md" />
              <Skeleton className="h-4 w-24" rounded="full" />
            </div>
          ))}
        </nav>
        <div className="border-t border-border-subtle p-3">
          <div className="flex items-center gap-3 rounded-md px-3 py-2">
            <Skeleton className="size-9 shrink-0" rounded="full" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-20" rounded="full" />
              <Skeleton className="mt-2 h-3 w-28" rounded="full" />
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header
          data-qsc-chrome="true"
          className="flex h-[calc(3.5rem+var(--safe-top))] shrink-0 items-center gap-3 border-b border-border-subtle bg-surface px-4 pt-[var(--safe-top)] lg:hidden"
        >
          <Skeleton className="size-10 shrink-0" rounded="md" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-5 w-24" rounded="md" />
            <Skeleton className="mt-2 h-3 w-16" rounded="full" />
          </div>
          <Skeleton className="size-10 shrink-0" rounded="md" />
        </header>
        <header
          data-qsc-chrome="true"
          className="hidden h-14 shrink-0 items-center justify-between border-b border-border-subtle bg-surface px-7 lg:flex"
        >
          <Skeleton className="h-5 w-24" rounded="md" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-7 w-24" rounded="full" />
            <Skeleton className="h-4 w-16" rounded="full" />
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-hidden">
          <HomeContentSkeleton />
        </main>
        <nav
          data-qsc-chrome="true"
          className="mobile-bottom-nav grid shrink-0 grid-cols-5 border-t border-border-subtle bg-surface lg:hidden"
        >
          {[0, 1, 2, 3, 4].map((key) => (
            <div key={key} className="flex min-w-0 flex-col items-center justify-center gap-1">
              <Skeleton className="size-5" rounded="md" />
              <Skeleton className="h-3 w-9" rounded="full" />
            </div>
          ))}
        </nav>
      </div>
    </div>
  )
}

export default function AuthGuard({ children }: Props) {
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn)
  const setToken = useAuthStore((s) => s.setToken)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const location = useLocation()
  const embedded = usesHostSession()
  const [waitingHostSession, setWaitingHostSession] = useState(() => embedded)
  const [hostSessionError, setHostSessionError] = useState<HostSessionError | null>(null)
  // 앱은 같은 메시지를 window·document 두 곳에 보낸다. 같은 토큰 검증이 진행 중이면 무시한다.
  const verifyingTokenRef = useRef<string | null>(null)
  const [hostSessionReady, setHostSessionReady] = useState(() => !embedded)
  const authReady = embedded ? hostSessionReady && isLoggedIn : isLoggedIn
  const goBack = () => {
    if (embedded) {
      postToHost({ type: 'GO_BACK' })
      return
    }
    if (window.history.length > 1) {
      navigate(-1)
      return
    }
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!embedded && !authReady) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname)}`, {
        replace: true,
      })
    }
  }, [authReady, embedded, navigate, location.pathname])


  useEffect(() => {
    if (!embedded) return undefined
    if (hostSessionError) return undefined
    if (hostSessionReady && isLoggedIn) {
      setWaitingHostSession(false)
      return undefined
    }

    setWaitingHostSession(true)
    let readyTimers: number[] = []
    const clearReadyTimers = () => readyTimers.forEach((timer) => window.clearTimeout(timer))

    // 리스너를 먼저 등록한 뒤 READY 를 보내야 앱의 SET_TOKEN 응답을 놓치지 않는다.
    const unlisten = listenFromHost(async (msg) => {
      if (msg.type !== 'SET_TOKEN') return
      const token = msg.payload.accessToken
      if (!token) {
        setHostSessionError({ kind: 'unverified', message: '앱에서 로그인 정보를 전달받지 못했습니다.' })
        setWaitingHostSession(false)
        return
      }
      if (verifyingTokenRef.current === token) return
      verifyingTokenRef.current = token
      clearReadyTimers()
      try {
        setToken(token, null)
        await fetchMe()
        setHostSessionReady(true)
        setWaitingHostSession(false)
      } catch (error) {
        setHostSessionReady(false)
        setWaitingHostSession(false)
        if (isApiError(error) && error.status === 401) {
          // SESSION_EXPIRED 는 http 인터셉터(handleUnauthorized)가 이미 보냈다.
          logout()
          setHostSessionError({ kind: 'expired', message: '다시 로그인한 후 이용해 주세요.' })
          return
        }
        // 인증 실패가 확인되지 않은 오류로는 로그인 정보를 지우거나 앱에 만료를 알리지 않는다.
        setHostSessionError({ kind: 'unverified', message: unverifiedMessage(error) })
      } finally {
        verifyingTokenRef.current = null
      }
    })

    postToHost({ type: 'READY' })
    readyTimers = [300, 900, 1_800].map((delay) => window.setTimeout(() => postToHost({ type: 'READY' }), delay))

    return () => {
      clearReadyTimers()
      unlisten()
    }
  }, [embedded, hostSessionError, hostSessionReady, isLoggedIn, logout, setToken])

  if (!authReady) {
    if (hostSessionError) {
      const unverified = hostSessionError.kind === 'unverified'
      // 오류를 지우면 위 effect 가 다시 돌면서 리스너 등록 → READY → 앱이 같은 토큰을 다시 보낸다.
      const retry = () => {
        setHostSessionError(null)
        setHostSessionReady(false)
        setWaitingHostSession(true)
      }
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg px-6 text-center">
          <div className="flex max-w-xs flex-col items-center rounded-card border border-border-subtle bg-surface px-6 py-5 shadow-card">
            <p className="text-preset-body-strong text-text">
              {unverified ? '로그인 상태를 확인하지 못했습니다' : '로그인 정보가 유효하지 않습니다'}
            </p>
            <p className="mt-2 text-preset-caption text-text-muted">{hostSessionError.message}</p>
            <button
              type="button"
              onClick={unverified ? retry : goBack}
              className="mt-5 h-10 rounded-md bg-brand px-5 text-preset-label font-semibold text-white transition hover:bg-brand-pressed"
            >
              {unverified ? '다시 시도' : '돌아가기'}
            </button>
          </div>
        </div>
      )
    }

    if (waitingHostSession) {
      return <AppSessionSkeleton contentOnly={hasHostShell()} />
    }

    return null
  }

  return <>{children}</>
}
